/**
 * The booked and request-sent pages at phone width (web-sign-in-checkout D12;
 * frames A to C of docs/mockups/telify-web/phone-booked.html): the app's end
 * screen (EndScreen, shared-client-screens D3), fed the same behavior halves as
 * the desktop pages. The slots the website fills: the line under the title says
 * where the confirmation went (D6, row K1); Add to Calendar is the menu
 * (Decision B) and Get directions opens a new tab (row K3); Done opens the
 * studio's home (row K4), replacing history and vibrating as the app's does (row
 * K4b); a request keeps Cancel request under it, confirmed in the app's sheet.
 */
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EndScreen, EndScreenLoading } from '@/pages/BookedEndPage/EndScreen';
import { bookingDetailHeroProps, endRows } from '@/pages/booking/shared/bookingRules';
import { useClientLock } from '@/hooks/useClientLock';

import { getClientTimezone } from '../format';
import { useWebBooking } from '../hooks/useWebBooking';
import { useBooked } from '../pieces/booked';
import type { BookedTelegramLine } from '../pieces/booked/contract';
import { useRequestSent } from '../pieces/request-sent/useRequestSent';
import { CALENDAR_MENU_PARTS } from '../ui/calendarMenuParts';
import css from './PhoneBooked.module.css';

function TelegramLine({ line }: { line: BookedTelegramLine }) {
  return (
    <>
      {line.text}
      {line.chatHref && (
        <>
          {' '}
          <a className={css.openChat} href={line.chatHref} target="_blank" rel="noopener noreferrer">
            Open chat
          </a>
        </>
      )}
    </>
  );
}

const openOutside = (href: string) => void window.open(href, '_blank', 'noopener');

/** Done: the studio's home, replacing this page in history, with the app's tap. */
function useDone() {
  const navigate = useNavigate();
  return {
    label: 'Done',
    onPress: () => {
      if (navigator.vibrate) navigator.vibrate(50);
      navigate('/', { replace: true });
    },
  };
}

export function PhoneBooked() {
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const b = useWebBooking(bookingId).detail.data;
  const { state, actions } = useBooked();
  const done = useDone();
  useClientLock();
  if (!b) return <EndScreenLoading />;
  const tz = getClientTimezone();
  return (
    <EndScreen
      hero={bookingDetailHeroProps(b, tz, false)}
      status={{ icon: 'check', title: 'Booking Confirmed!', lines: state.telegram ? [<TelegramLine key="tg" line={state.telegram} />] : [] }}
      rows={endRows(b, tz, false)}
      calendar={state.calendar && { choices: state.calendar, onApple: actions.addToCalendar, parts: CALENDAR_MENU_PARTS }}
      onDirections={openOutside}
      done={done}
    />
  );
}

export function PhoneRequestSent() {
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const b = useWebBooking(bookingId).detail.data;
  const { state, actions } = useRequestSent();
  const done = useDone();
  useClientLock();
  if (!b) return <EndScreenLoading />;
  const tz = getClientTimezone();
  const lines: ReactNode[] = [state.lead];
  if (state.telegram) lines.push(<TelegramLine key="tg" line={state.telegram} />);
  if (state.problem) lines.push(<span key="problem" role="alert">{state.problem}</span>);
  return (
    <EndScreen
      hero={bookingDetailHeroProps(b, tz, true)}
      cancelled={state.cancelled}
      status={{ icon: state.cancelled ? 'close' : 'clock', title: state.cancelled ? 'Request cancelled' : 'Request sent', lines }}
      rows={endRows(b, tz, true)}
      calendar={state.cancelled || !state.calendar ? null : { choices: state.calendar, onApple: actions.addToCalendar, parts: CALENDAR_MENU_PARTS }}
      onDirections={openOutside}
      done={done}
      cancel={
        state.canCancel || state.cancelling
          ? {
              dateLabel: new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: tz }).format(new Date(b.scheduledAt)),
              providerName: b.provider?.name ?? null,
              pending: state.cancelling,
              onConfirm: actions.cancel,
            }
          : null
      }
    />
  );
}
